import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, eur, errMsg } from "@/lib/api";

const STATUSES = ["in attesa di pagamento", "pagato", "in preparazione", "spedito", "consegnato", "annullato", "reso in corso", "rimborsato"];
const PAY = { pending: "in attesa", paid: "pagato", expired: "scaduto", failed: "fallito", refunded: "rimborsato", partially_refunded: "rimborsato in parte" };
const input = "h-9 rounded-lg border border-white/10 bg-white/5 px-2 text-sm outline-none focus:border-[#0066FF]";

const OrderCard = ({ o, onChange }) => {
  const [t, setT] = useState({ carrier: o.carrier || "", tracking: o.tracking || "", tracking_url: o.tracking_url || "" });
  const [refund, setRefund] = useState({ amount: "", reason: "" });
  const run = async (fn, ok) => {
    try {
      const r = await fn();
      if (r?.data?.id) onChange(r.data);
      toast.success(ok);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const c = o.customer || {};
  return (
    <details className="rounded-2xl border border-white/10 bg-[#12141A] p-4 text-sm" data-testid={`admin-order-${o.number}`}>
      <summary className="flex cursor-pointer flex-wrap items-center gap-3">
        <span className="font-mono text-xs">{o.number}</span>{o.test && <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs text-amber-200">TEST</span>}
        <span>{c.name}</span><span className="text-slate-400">{c.email}</span>
        <span className="ml-auto font-mono">{eur(o.total)}</span><span className="text-xs text-slate-400">{o.status} · pagamento {PAY[o.payment_status] || o.payment_status}</span>
      </summary>
      <div className="mt-4 grid gap-6 lg:grid-cols-2">
        <div className="space-y-1 text-slate-300">
          <p>{c.address}, {c.zip} {c.city} ({c.province}) · Tel. {c.phone}</p>{c.notes && <p>Note: {c.notes}</p>}
          <ul className="mt-2 border-t border-white/10 pt-2">{o.items.map((i, k) => <li key={k}>{i.name} × {i.qty} — {eur(i.line_total)}{i.options.length > 0 && <span className="text-xs text-slate-500"> ({i.options.map((x) => x.value).join(", ")})</span>}</li>)}</ul>
          <p>{o.shipping.name}: {eur(o.shipping.price)}{o.coupon && ` · Coupon ${o.coupon.code}: -${eur(o.coupon.amount)}`}</p>
          {o.refunds?.length > 0 && <p className="text-amber-200">Rimborsi: {o.refunds.map((r) => `${eur(r.amount)} (${r.reason || "-"})`).join(", ")}</p>}
          <p className="text-xs text-slate-500">{(o.history || []).map((h) => `${new Date(h.at).toLocaleString("it-IT")} ${h.event}`).join(" · ")}</p>
        </div>
        <div className="space-y-3">
          <select value={o.status} onChange={(e) => run(() => api.patch(`/admin/orders/${o.id}`, { status: e.target.value }), "Stato aggiornato")} className={input + " w-full bg-[#0A0B0E]"} data-testid={`order-status-${o.number}`}>
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <input className={input} value={t.carrier} onChange={(e) => setT({ ...t, carrier: e.target.value })} placeholder="Corriere" data-testid={`order-carrier-${o.number}`} />
            <input className={input} value={t.tracking} onChange={(e) => setT({ ...t, tracking: e.target.value })} placeholder="Codice tracking" data-testid={`order-tracking-${o.number}`} />
            <input className={input + " col-span-2"} value={t.tracking_url} onChange={(e) => setT({ ...t, tracking_url: e.target.value })} placeholder="https://link-tracking" data-testid={`order-tracking-url-${o.number}`} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => run(() => api.patch(`/admin/orders/${o.id}`, t), "Tracking salvato")} className="rounded-full border border-white/15 px-3 py-1.5 text-xs" data-testid={`order-save-tracking-${o.number}`}>Salva tracking</button>
            <button onClick={() => run(() => api.post(`/admin/orders/${o.id}/notify-tracking`), "Email tracking inviata")} className="rounded-full bg-[#0066FF] px-3 py-1.5 text-xs" data-testid={`order-notify-${o.number}`}>Invia email tracking</button>
          </div>
          {["paid", "partially_refunded"].includes(o.payment_status) && o.payment_intent && (
            <div className="flex gap-2 border-t border-white/10 pt-3">
              <input className={input + " w-28"} type="number" step="0.01" value={refund.amount} onChange={(e) => setRefund({ ...refund, amount: e.target.value })} placeholder="€ rimborso" data-testid={`order-refund-amount-${o.number}`} />
              <input className={input + " flex-1"} value={refund.reason} onChange={(e) => setRefund({ ...refund, reason: e.target.value })} placeholder="Motivo (recesso, non conformità…)" />
              <button onClick={() => window.confirm(`Rimborsare ${refund.amount} € tramite Stripe?`) && run(() => api.post(`/admin/orders/${o.id}/refund`, { amount: Number(refund.amount), reason: refund.reason }), "Rimborso eseguito").then(() => setRefund({ amount: "", reason: "" }))}
                className="rounded-full border border-red-500/40 px-3 py-1.5 text-xs text-red-300" data-testid={`order-refund-btn-${o.number}`}>Rimborsa</button>
            </div>
          )}
        </div>
      </div>
    </details>
  );
};

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  useEffect(() => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch((e) => {
      toast.error(errMsg(e));
      setOrders([]);
    });
  }, []);
  return (
    <div className="max-w-6xl" data-testid="admin-orders-page">
      <h1 className="font-display text-3xl font-semibold">Ordini, pagamenti e rimborsi</h1>
      <p className="mt-2 text-sm text-slate-400">Gli ordini diventano "pagato" solo dopo la verifica del pagamento da parte di Stripe. I rimborsi sono eseguiti tramite Stripe.</p>
      {orders && !orders.length && <div className="mt-6 rounded-2xl border border-dashed border-white/15 p-12 text-center text-sm text-slate-400" data-testid="admin-orders-empty">Nessun ordine.</div>}
      <div className="mt-6 space-y-3">{(orders || []).map((o) => <OrderCard key={o.id} o={o} onChange={(n) => setOrders((xs) => xs.map((x) => (x.id === n.id ? n : x)))} />)}</div>
    </div>
  );
}
